import { zodResolver } from '@hookform/resolvers/zod';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { FormDrawer } from '@/components/feedback/drawer';
import { useToast } from '@/components/feedback/toast';
import { PhoneField, SelectField, TextField } from '@/components/forms';
import { ApiError } from '@/lib/api/errors';

import { CREATABLE_ROLE_OPTIONS } from '../constants';
import { buildUserFormSchema, USER_FORM_FIELDS } from '../schemas';
import type { UserFormValues } from '../types';

const EMPTY = { firstName: '', lastName: '', phone: '', email: '', role: undefined };

/** One flattened class-validator failure: `setup-app.ts`'s `exceptionFactory` emits
 *  `{ field, message }[]` and `validation.filter.ts` sends it as the envelope's `details`. */
type ValidationDetail = { field: string; message: string };

const isUserFormField = (field: string): field is keyof UserFormValues =>
  USER_FORM_FIELDS.includes(field);

export type AdminUserFormDrawerProps = {
  open: boolean;
  mode?: 'create' | 'edit';
  initialValues?: UserFormValues;
  onSubmit: (values: UserFormValues) => Promise<void>;
  onClose: () => void;
};

export function AdminUserFormDrawer({
  open,
  mode = 'create',
  initialValues,
  onSubmit,
  onClose,
}: AdminUserFormDrawerProps) {
  const toast = useToast();
  // Edit: an unchanged stored phone that predates the NANP rule must not block Save (ADR-0035).
  const storedPhone = mode === 'edit' ? initialValues?.phone : undefined;
  const schema = useMemo(() => buildUserFormSchema(storedPhone), [storedPhone]);
  const form = useForm<UserFormValues>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: initialValues ?? EMPTY,
  });

  // Reseed ONLY on the closed→open transition — never on `initialValues` identity alone. The page
  // passes a fresh object each render (rowToForm(editing)) and re-renders mid-submit as the
  // mutation state flips (idle→pending→success); resetting then would fire while the drawer is
  // still open and revert the user's edits to the stale row — the "old value" flash — and clobber
  // `isSubmitting`, killing the Save button loader. The `wasOpen` guard limits reset to real opens.
  const wasOpen = useRef(false);
  // A seeded Edit form is validated at once so a stored value today's rules refuse names itself
  // (ADO 121001) instead of silently disabling Save under `mode: 'onTouched'`. An unchanged phone is
  // exempt (ADR-0035), so this now catches the other fields. Create has no seed, so is never flagged.
  useLayoutEffect(() => {
    if (open && !wasOpen.current) {
      form.reset(initialValues ?? EMPTY);
      if (initialValues) void form.trigger();
    }
    wasOpen.current = open;
  }, [open, initialValues, form]);

  const handleSubmit = async (values: UserFormValues) => {
    try {
      await onSubmit(values);
      onClose();
    } catch (error) {
      if (error instanceof ApiError && error.code === 'USER_PHONE_EXISTS') {
        form.setError('phone', { message: 'This phone number already exists.' });
        return;
      }
      if (error instanceof ApiError && error.code === 'USER_EMAIL_EXISTS') {
        form.setError('email', { message: 'This email address already exists.' });
        return;
      }
      // Any other server-side field rejection lands on the offending input rather than being
      // replaced by the generic toast (ADO 118281). Mapped generically, so a DTO rule added later
      // needs no client change; `stopAtFirstError: true` on the backend means one message per
      // property. A detail naming a field this form does not render cannot be shown inline, so it
      // falls through to the toast below rather than setting an error nothing can display.
      if (error instanceof ApiError && error.code === 'VALIDATION_FAILED') {
        const details = (Array.isArray(error.details) ? error.details : []) as ValidationDetail[];
        let mapped = false;
        for (const detail of details) {
          if (!isUserFormField(detail.field)) continue;
          form.setError(detail.field, { message: detail.message });
          mapped = true;
        }
        if (mapped) return;
      }
      toast.error({ title: 'Something went wrong. Please try again.' });
    }
  };

  return (
    <FormDrawer
      form={form}
      open={open}
      title={mode === 'edit' ? 'Edit User' : 'Create User'}
      submitText={mode === 'edit' ? 'Save' : 'Create'}
      primaryDisabled={!form.formState.isValid}
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <TextField
        name="firstName"
        control={form.control}
        label="First Name"
        placeholder="Enter"
        maxLength={50}
        required
      />
      <TextField
        name="lastName"
        control={form.control}
        label="Last Name"
        placeholder="Enter"
        maxLength={50}
        required
      />
      <PhoneField name="phone" control={form.control} label="Phone Number" required />
      <SelectField
        name="role"
        control={form.control}
        label="Role"
        placeholder="Select"
        options={CREATABLE_ROLE_OPTIONS}
        required
      />
      <TextField
        name="email"
        control={form.control}
        label="Email"
        placeholder="Enter"
        maxLength={100}
        required
      />
    </FormDrawer>
  );
}
